import {
  Group,
  Menu,
  useMatches,
  FloatingPosition,
  Button,
  Avatar,
  Switch,
  MenuProps,
} from "@mantine/core"
import {
  IconLogout,
  IconSettings,
  IconSwitchHorizontal,
  IconUser,
} from "@tabler/icons-react"
import { ColorToggle } from "./color-toggle"
import { useQuery, useQueryClient } from "@tanstack/react-query"
import {
  showSuccessNotification,
  showErrorNotification,
} from "../../../utils/form-submission-notifications"
import { useNavigate } from "@tanstack/react-router"
import { HideOnProd } from "../../../-components/hide-on-environment.tsx"
import { LanguageSelect } from "./language-select"
import { useTranslation } from "react-i18next"
import { LoginButton } from "./login-button.tsx/index.tsx"
import { PersonProfilePictureAvatar } from "../../app/-components/person-profile-picture-avatar"
import { useGetAuthenticatedUser } from "../../app/-components/use-authenticated-user.tsx"
import { usePostMutation } from "../../../typed-client/hooks/use-post-mutation.ts"
import {
  ensureClientPushSubscription,
  getClientPushSubscription,
  unsubscribeClientToPushNotification,
} from "../../../utils/subscriptions.ts"
import { Suspense } from "react"
import { openConfirmModal } from "@mantine/modals"
import { useUserLocalStorage } from "@/routes/app/-components/use-user-local-storage.tsx"
import { userNotificationsEnabeledKey } from "@/utils/local-storage-keys.ts"

interface Props extends MenuProps {
  iconOnly?: boolean
  onClick?: () => void
  onNavigate?: () => void
}

export function UserSectionContent({
  iconOnly,
  onClick,
  onNavigate,
  ...menuProps
}: Props) {
  const client = useQueryClient()

  const navigate = useNavigate()

  const { mutateAsync: logout } = usePostMutation(
    "/api/Authentication/Logout",
    {
      onMutate: async () => {
        await unsubscribeClientToPushNotification()
      },
      onSuccess: () => {
        client.invalidateQueries()
        showSuccessNotification("Logged out")

        location.href = "/app"
      },
      onError: showErrorNotification,
    }
  )

  const position: FloatingPosition = useMatches({
    base: "top",
    sm: "right",
  })

  const { t } = useTranslation("app_server")

  const { data: authenticatedUser } = useGetAuthenticatedUser()

  const displayName =
    authenticatedUser.person?.name ?? authenticatedUser.userName

  return (
    <>
      <Group
        wrap={iconOnly ? "wrap-reverse" : "nowrap"}
        display="flex"
        gap={0}
        w="100%"
      >
        {authenticatedUser ? null : (
          <LoginButton
            flex="1 1 80%"
            size="xl"
            style={{
              borderRadius: 0,
            }}
            p={authenticatedUser && iconOnly ? "xs" : "md"}
            justify="space-between"
            fw={400}
            variant="subtle"
            styles={{
              inner: {
                justifyContent: "flex-start",
              },
            }}
            onClick={() =>
              navigate({
                to: "/authenticate/login",
                search: { returnUrl: undefined },
              })
            }
            iconOnly={iconOnly}
          />
        )}
        <Menu
          trigger="click-hover"
          position={position}
          arrowSize={15}
          width={position === "right" ? undefined : "90%"}
          shadow="2"
          withArrow
          closeOnItemClick
          openDelay={100}
          closeDelay={400}
          {...menuProps}
        >
          <Menu.Target>
            <Button
              style={{
                borderRadius: 0,
                overflow: "visible",
              }}
              styles={{
                inner: {
                  alignItems: "center",
                  overflow: "visible",
                },
                label: {
                  lineHeight: 1.2,
                  overflow: "visible",
                },
              }}
              justify={"space-between"}
              variant="subtle"
              flex="1 1 20%"
              fw={400}
              p={authenticatedUser && iconOnly ? "xs" : "md"}
              size="lg"
              rightSection={
                authenticatedUser && !iconOnly ? <IconSettings /> : null
              }
              leftSection={
                authenticatedUser ? (
                  <Suspense fallback={<Avatar />}>
                    <PersonProfilePictureAvatar
                      personId={authenticatedUser.personId ?? 0}
                      name={displayName}
                    />
                  </Suspense>
                ) : null
              }
            >
              {authenticatedUser ? displayName : <IconSettings />}
            </Button>
          </Menu.Target>
          <Menu.Dropdown>
            {authenticatedUser ? (
              <>
                <Menu.Label>{t("Account")}</Menu.Label>
                <Menu.Item
                  onClick={() => {
                    navigate({ to: "/app/account" })
                    onClick && onClick()
                    onNavigate && onNavigate()
                  }}
                  leftSection={<IconUser stroke={1} />}
                >
                  {t("ViewProfile")}
                </Menu.Item>
                <HideOnProd>
                  <Menu.Item
                    leftSection={<IconSwitchHorizontal stroke={1} />}
                    onClick={() => {
                      onClick && onClick()
                      onNavigate && onNavigate()
                      navigate({
                        to: "/authenticate/login",
                        search: { returnUrl: undefined },
                      })
                    }}
                  >
                    {t("ChangeUser")}
                  </Menu.Item>
                </HideOnProd>
                <Menu.Item
                  leftSection={<IconLogout stroke={1} />}
                  onClick={() => {
                    openConfirmModal({
                      title: t("ConfirmLogout"),
                      labels: {
                        cancel: t("Cancel"),
                        confirm: t("Logout"),
                      },
                      onConfirm: async () => await logout(null),
                      onClose: menuProps.onClose,
                    })
                    onClick && onClick()
                  }}
                >
                  {t("Logout")}
                </Menu.Item>
              </>
            ) : null}
            <Menu.Label>{t("Theme")}</Menu.Label>
            <Menu.Item component={ColorToggle} />
            <Menu.Label>{t("Notifications")}</Menu.Label>
            <Menu.Item onClick={() => onClick && onClick()}>
              <NotificationSwitch />
            </Menu.Item>
            <Menu.Label>{t("Language")}</Menu.Label>
            <Menu.Item component={LanguageSelect} />
          </Menu.Dropdown>
        </Menu>
      </Group>
    </>
  )
}

function NotificationSwitch() {
  const [notficationPermission, setNotificationPermission] =
    React.useState<NotificationPermission>(
      window && window["Notification"]
        ? window.Notification.permission
        : "denied"
    )
  const { t } = useTranslation("app_server")
  const { data: subscription, refetch: refetchSubscription } = useQuery({
    queryKey: [getClientPushSubscription.name],
    queryFn: getClientPushSubscription,
  })
  const pushEnabled = notficationPermission === "granted" && !!subscription
  const [userNotificationsEnabled, setUserNotificationsEnabled] =
    useUserLocalStorage({
      key: userNotificationsEnabeledKey,
      defaultValue: false,
    })

  const handleToggleNotdifications = async (enable: boolean) => {
    if (enable) {
      setUserNotificationsEnabled(true)
      if (!pushEnabled) {
        const perm = await window.Notification.requestPermission()
        setNotificationPermission(perm)

        if (perm === "granted") {
          try {
            await ensureClientPushSubscription()
            refetchSubscription()
          } catch (err) {
            console.error("Error in subscription:", err)
          }
        }
      }
    } else {
      setUserNotificationsEnabled(false)
      if (pushEnabled) {
        await unsubscribeClientToPushNotification()
        refetchSubscription()
        setNotificationPermission("denied")
      }
    }
  }

  return (
    <Switch
      p="sm"
      label={userNotificationsEnabled ? t("On") : t("Off")}
      checked={userNotificationsEnabled}
      onChange={async () => {
        await handleToggleNotdifications(!userNotificationsEnabled)
      }}
    />
  )
}
